import { c as _c } from "react/compiler-runtime";
import type { VaultValue } from '@onefootprint/core';
import { IcoInfo16, type Icon } from '@onefootprint/icons';
import type { DataIdentifier } from '@onefootprint/request-types/dashboard';
import { Divider, LinkButton } from '@onefootprint/ui';
import type React from 'react';
import { Fragment, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDecryptForm } from '@/entity/components/decrypt-form';
import type { WithEntityProps } from '@/entity/components/with-entity';
import useDecryptControls from '@/entity/hooks/use-decrypt-controls';
import useField from '../../hooks/use-field';
import type { DiField } from '../../vault.types';
import Field from '../field';
import StreetViewDialog from './components/street-view-dialog';
export type AddressFieldsetProps = WithEntityProps & {
  children?: React.ReactNode;
  fields: DiField[];
  footer?: React.ReactNode;
  iconComponent: Icon;
  title: string;
};
const AddressFieldset = t0 => {
  const $ = _c(61);
  const {
    children,
    entity,
    fields,
    footer,
    iconComponent: IconComponent,
    title
  } = t0;
  let t1;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t1 = {
      keyPrefix: "fieldset"
    };
    $[0] = t1;
  } else {
    t1 = $[0];
  }
  const {
    t
  } = useTranslation("entity-details", t1);
  const [streetViewDialogOpen, setStreetViewDialogOpen] = useState(false);
  const decryptControls = useDecryptControls();
  const decryptForm = useDecryptForm();
  let t2;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== decryptForm.control) {
    t3 = {
      control: decryptForm.control,
      name: "fields",
      defaultValue: t2
    };
    $[2] = decryptForm.control;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  const selectedFields = useWatch(t3);
  const dis = fields.map(_temp);
  const getFieldProps = useField(entity);
  let t4;
  if ($[4] !== getFieldProps) {
    t4 = di => getFieldProps(di).canSelect;
    $[4] = getFieldProps;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const selectableFields = dis.filter(t4);
  let t5;
  if ($[6] !== selectedFields) {
    t5 = field_0 => selectedFields.includes(field_0);
    $[6] = selectedFields;
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const allSelected = selectableFields.every(t5);
  const hasSelectableFields = selectableFields.length > 0;
  const shouldShowSelectAll = decryptControls.step === "selecting-fields" && hasSelectableFields;
  let t6;
  if ($[8] !== fields || $[9] !== getFieldProps) {
    let t7;
    if ($[11] !== getFieldProps) {
      t7 = field_1 => {
        const value = getFieldProps(field_1.di).value;
        return [field_1.di, typeof value === "string" ? value : ""];
      };
      $[11] = getFieldProps;
      $[12] = t7;
    } else {
      t7 = $[12];
    }
    t6 = Object.fromEntries(fields.map(t7));
    $[8] = fields;
    $[9] = getFieldProps;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  const addressValues = t6;
  let t7;
  if ($[13] !== decryptForm || $[14] !== selectableFields) {
    t7 = () => {
      decryptForm.setFields(selectableFields);
    };
    $[13] = decryptForm;
    $[14] = selectableFields;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const handleSelectAll = t7;
  let t8;
  if ($[16] !== decryptForm || $[17] !== selectableFields) {
    t8 = () => {
      decryptForm.unsetFields(selectableFields);
    };
    $[16] = decryptForm;
    $[17] = selectableFields;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  const handleDeselectAll = t8;
  let t9;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      setStreetViewDialogOpen(false);
    };
    $[19] = t9;
  } else {
    t9 = $[19];
  }
  const closeStreetViewDialog = t9;
  let t10;
  if ($[20] !== entity) {
    t10 = field_2 => {
      const {
        di: di_0,
        renderCustomField
      } = field_2;
      return renderCustomField ? <Fragment key={di_0}>{renderCustomField({
          entity,
          di: di_0
        })}</Fragment> : <Field key={di_0} di={di_0} entity={entity} />;
    };
    $[20] = entity;
    $[21] = t10;
  } else {
    t10 = $[21];
  }
  const renderField = t10;
  let t11;
  if ($[22] !== addressValues || $[23] !== t) {
    t11 = () => {
      const validAddressValues = Object.values(addressValues).filter(_temp2);
      const hasDecryptedFullAddress = validAddressValues.length >= 5;
      if (hasDecryptedFullAddress) {
        return <LinkButton onClick={() => setStreetViewDialogOpen(true)}>{t(_temp3)}</LinkButton>;
      }
      return <div className="flex items-center gap-2"><IcoInfo16 color="tertiary" /><div className="text-body-2 text-tertiary">{t(_temp4)}</div></div>;
    };
    $[22] = addressValues;
    $[23] = t;
    $[24] = t11;
  } else {
    t11 = $[24];
  }
  const renderStreetViewToggle = t11;
  let t12;
  if ($[25] !== IconComponent) {
    t12 = <IconComponent />;
    $[25] = IconComponent;
    $[26] = t12;
  } else {
    t12 = $[26];
  }
  let t13;
  if ($[27] !== t12 || $[28] !== title) {
    t13 = <h2 className="flex items-center gap-2 text-label-2 text-primary">{t12}{title}</h2>;
    $[27] = t12;
    $[28] = title;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  let t14;
  if ($[30] !== allSelected || $[31] !== handleDeselectAll || $[32] !== handleSelectAll || $[33] !== shouldShowSelectAll || $[34] !== t) {
    t14 = shouldShowSelectAll && <LinkButton onClick={allSelected ? handleDeselectAll : handleSelectAll}>{allSelected ? t(_temp5) : t(_temp6)}</LinkButton>;
    $[30] = allSelected;
    $[31] = handleDeselectAll;
    $[32] = handleSelectAll;
    $[33] = shouldShowSelectAll;
    $[34] = t;
    $[35] = t14;
  } else {
    t14 = $[35];
  }
  let t15;
  if ($[36] !== t13 || $[37] !== t14) {
    t15 = <header className="flex justify-between px-4 py-2 border-b border-solid border-tertiary rounded-t-[sm] bg-secondary h-[40px]">{t13}{t14}</header>;
    $[36] = t13;
    $[37] = t14;
    $[38] = t15;
  } else {
    t15 = $[38];
  }
  let t16;
  if ($[39] !== children || $[40] !== fields || $[41] !== renderField) {
    t16 = children || fields.map(renderField);
    $[39] = children;
    $[40] = fields;
    $[41] = renderField;
    $[42] = t16;
  } else {
    t16 = $[42];
  }
  let t17;
  if ($[43] !== t16) {
    t17 = <div className="flex flex-col gap-3">{t16}</div>;
    $[43] = t16;
    $[44] = t17;
  } else {
    t17 = $[44];
  }
  let t18;
  if ($[45] !== renderStreetViewToggle) {
    t18 = renderStreetViewToggle();
    $[45] = renderStreetViewToggle;
    $[46] = t18;
  } else {
    t18 = $[46];
  }
  let t19;
  if ($[47] !== t17 || $[48] !== t18) {
    t19 = <div className="flex flex-col gap-6 p-4 flex-1">{t17}{t18}</div>;
    $[47] = t17;
    $[48] = t18;
    $[49] = t19;
  } else {
    t19 = $[49];
  }
  let t20;
  if ($[50] !== addressValues || $[51] !== streetViewDialogOpen) {
    t20 = streetViewDialogOpen && <StreetViewDialog onClose={closeStreetViewDialog} addressValues={addressValues} />;
    $[50] = addressValues;
    $[51] = streetViewDialogOpen;
    $[52] = t20;
  } else {
    t20 = $[52];
  }
  let t21;
  if ($[53] !== footer) {
    t21 = footer && <footer className="flex flex-col gap-3 px-4 pb-3"><Divider />{footer}</footer>;
    $[53] = footer;
    $[54] = t21;
  } else {
    t21 = $[54];
  }
  let t22;
  if ($[55] !== t15 || $[56] !== t19 || $[57] !== t20 || $[58] !== t21 || $[59] !== title) {
    t22 = <fieldset aria-label={title} className="w-full h-full flex flex-col justify-between border border-solid border-tertiary rounded-sm">{t15}{t19}{t20}{t21}</fieldset>;
    $[55] = t15;
    $[56] = t19;
    $[57] = t20;
    $[58] = t21;
    $[59] = title;
    $[60] = t22;
  } else {
    t22 = $[60];
  }
  return t22;
};
export default AddressFieldset;
function _temp(field) {
  return field.di;
}
function _temp2(value_0) {
  return !!value_0;
}
function _temp3($) {
  return $.address.streetView.seeStreetView;
}
function _temp4($_0) {
  return $_0.address.streetView.decryptData;
}
function _temp5($_1) {
  return $_1.deselectAll;
}
function _temp6($_2) {
  return $_2.selectAll;
}